import React, { useEffect, useMemo, useState } from "react";
import { fetchDashboardSites } from "../api/dashboard";
import ProductionChart from "../ui/ProductionChart";

const statusTone = {
  "On Grid": "bg-emerald-100 text-emerald-800 border-emerald-200",
  "Standby - No irradiation": "bg-amber-100 text-amber-800 border-amber-200",
  Idle: "bg-slate-100 text-slate-700 border-slate-200",
  "No data": "bg-slate-200 text-slate-600 border-slate-300",
};

const formatEnergy = (kwh) => {
  const value = Number(kwh);
  if (!Number.isFinite(value)) return "—";
  if (Math.abs(value) >= 1000) return `${(value / 1000).toFixed(2)} MWh`;
  return `${value.toFixed(1)} kWh`;
};

const formatCapacity = (value) => {
  if (value === null || value === undefined) return "—";
  return Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const formatTime = (value, tz) => {
  if (!value) return "—";
  return new Date(value).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: tz || "UTC",
  });
};

const formatTemp = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return `${num.toFixed(1)}°C`;
};

function ForecastList({ forecast, timezone }) {
  if (!forecast?.length) {
    return <div className="text-sm text-slate-500">No forecast available.</div>;
  }
  return (
    <div className="space-y-2">
      {forecast.map((item, idx) => (
        <div
          key={item.timestamp || idx}
          className="flex items-center justify-between rounded-lg border border-sky-100 bg-white/70 px-3 py-1.5 shadow-sm"
        >
          <div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500">Hour</div>
            <div className="text-sm font-semibold text-slate-800">
              {formatTime(item.timestamp, timezone)}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {item.icon_url ? (
              <img src={item.icon_url} alt={item.description || "weather"} className="h-8 w-8" />
            ) : (
              <div className="h-8 w-8 rounded-full bg-slate-100" />
            )}
            <div className="text-right">
              <div className="text-base font-semibold text-slate-900 leading-tight">
                {formatTemp(item.temp)}
              </div>
              <div className="text-[11px] text-slate-500 capitalize leading-tight">
                {item.description || "—"}
              </div>
              <div className="text-[10px] text-slate-400 leading-tight">
                Clouds {item.clouds ?? "—"}% · Wind {item.wind_speed ?? "—"} m/s
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function DashboardCard({ site }) {
  const tone = statusTone[site.status] || statusTone["No data"];

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-lg shadow-slate-200/70 overflow-hidden">
      <div className="grid gap-5 p-6 lg:grid-cols-3">
        {/* Left - site info + summary + current weather */}
        <div className="flex flex-col gap-4 rounded-xl border border-slate-100 bg-gradient-to-br from-white via-slate-50 to-slate-100 p-4 shadow-inner">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">Site</p>
              <h2 className="text-xl font-semibold text-slate-900 leading-tight">{site.site_name}</h2>
              <p className="text-sm text-slate-500">Timezone: {site.timezone}</p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className={`flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${tone}`}>
                <span className="h-2 w-2 rounded-full bg-current" />
                {site.status}
              </div>
              <div className="text-right text-sm">
                <div className="text-[10px] uppercase tracking-wide text-slate-500">Nominal AC</div>
                <div className="font-semibold text-slate-900">
                  {formatCapacity(site.ac_nominal_capacity)} MW
                </div>
                <div className="text-[10px] uppercase tracking-wide text-slate-500 mt-1">Nominal DC</div>
                <div className="font-semibold text-slate-900">
                  {formatCapacity(site.dc_nominal_capacity)} MW
                </div>
              </div>
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500">Today's yield</div>
            <div className="text-3xl font-semibold text-slate-900">{formatEnergy(site.energy_today_kwh)}</div>
            <div className="text-sm text-slate-500">
              Expected {formatEnergy(site.expected_today_kwh)}
            </div>
          </div>
          {site.current_weather ? (
            <div className="rounded-lg border border-sky-100 bg-white/70 p-3 shadow-sm">
              <div className="flex items-center gap-3">
                {site.current_weather.icon_url ? (
                  <img
                    src={site.current_weather.icon_url}
                    alt={site.current_weather.description || "Weather"}
                    className="h-12 w-12"
                  />
                ) : (
                  <div className="h-12 w-12 rounded-full bg-slate-200" />
                )}
                <div>
                  <div className="text-sm uppercase tracking-wide text-slate-500">Current weather</div>
                  <div className="text-lg font-semibold text-slate-900">
                    {formatTemp(site.current_weather.temp)}
                  </div>
                  <div className="text-sm text-slate-500 capitalize">
                    {site.current_weather.description || "—"}
                  </div>
                </div>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-slate-600">
                <div className="rounded-md bg-slate-50 p-2">
                  Sunrise
                  <div className="font-semibold text-slate-800">
                    {formatTime(site.current_weather.sunrise, site.timezone)}
                  </div>
                </div>
                <div className="rounded-md bg-slate-50 p-2">
                  Sunset
                  <div className="font-semibold text-slate-800">
                    {formatTime(site.current_weather.sunset, site.timezone)}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-slate-200 p-3 text-sm text-slate-500">
              Weather data has not been received yet for this site.
            </div>
          )}
        </div>

        {/* Middle - chart */}
        <div className="rounded-xl border border-slate-100 bg-white p-4 shadow-inner">
          <div className="mb-2 flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-500">Today's production</div>
              <div className="text-lg font-semibold text-slate-900">Real vs theoretical (per hour)</div>
            </div>
          </div>
          <ProductionChart
            buckets={site.energy_buckets}
            height={320}
            sunrise={site.current_weather?.sunrise}
            sunset={site.current_weather?.sunset}
          />
        </div>

        {/* Right - forecast */}
        <div className="rounded-xl border border-sky-100 bg-gradient-to-br from-sky-50 via-white to-sky-50 p-4 shadow-inner">
          <div className="mb-3">
            <div className="text-xs uppercase tracking-wide text-slate-500">Forecast</div>
            <div className="text-lg font-semibold text-slate-900">Next 5 hours</div>
          </div>
          <ForecastList forecast={site.forecast} timezone={site.timezone} />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("");

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchDashboardSites();
      setSites(data);
    } catch (err) {
      setError(err?.message || "Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredSites = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return sites;
    return sites.filter((site) => site.site_name.toLowerCase().includes(needle));
  }, [filter, sites]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Portfolio</p>
          <h1 className="text-3xl font-semibold text-slate-900">Dashboard</h1>
          <p className="text-slate-600">
            Live view of site health, production, and on-the-ground weather.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <a
            href="https://openweathermap.org/"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 text-[11px] text-slate-500 hover:text-slate-700 transition"
          >
            <span>Weather data provided by OpenWeather</span>
            <img
              src="/openweather-logo.png"
              alt="OpenWeather"
              className="h-10 w-auto opacity-80"
            />
          </a>
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <input
                type="search"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filter by site name"
                className="rounded-full border border-slate-200 bg-white px-4 py-2 pr-10 text-sm shadow-inner focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">⌕</span>
            </div>
            <button
              onClick={loadData}
              className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-slate-300 transition hover:bg-slate-800"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {loading && <div className="text-slate-500">Loading dashboard…</div>}
      {error && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-800">
          {error}
        </div>
      )}

      {!loading && !error && !filteredSites.length && (
        <div className="rounded-lg border border-slate-200 bg-white p-4 text-slate-600 shadow">
          No sites found. Add a site with at least one array to see it here.
        </div>
      )}

      {!loading && !error && filteredSites.map((site) => <DashboardCard key={site.site_id} site={site} />)}
    </div>
  );
}
